import type { NavbarWithCtaButton } from '@flotiq/flotiq-flowbite';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { _translate } from '@flotiq/nextjs-internationalization';

import { getEnv } from '../../lib/env';
import { getFlotiqPageFooter } from '../../lib/flotiq-client/external-services-requests';
import { parseResponseErrors } from '../../lib/flotiq-client/response-errors';
import { getFlotiqMainPageUrl } from '../../lib/helpers';

import footerPlaceholderData from './placeholder-data/footer.json';

const QUERY_KEY = ['flotiq_page_space', 'footer'];

export const useFooter = () => {
  const {
    isLoading: requestLoading,
    data: response,
    isPlaceholderData,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async ({ signal }) => {
      const result = await getFlotiqPageFooter(
        undefined,
        undefined,
        { hydrate: 1 },
        {
          signal,
        },
      );
      if (!result.ok) {
        throw parseResponseErrors(result.body, result.status);
      }
      return result;
    },
    enabled:
      !!getEnv('VITE_FLOTIQ_PAGE_SPACE_X_AUTH_TOKEN') &&
      !!getEnv('VITE_FLOTIQ_PAGE_FOOTER_API_URL'),
    staleTime: Infinity,
    gcTime: 0,
    retry: 0,
  });

  const { i18n } = useTranslation();

  const isLoading = requestLoading || isPlaceholderData;

  const footerData = response?.ok
    ? _translate<typeof NavbarWithCtaButton>(response.body, i18n.language)
    : footerPlaceholderData;

  const finalFooter =
    'column' in footerData
      ? {
          ...footerData,
          column: footerData.column.map((item) => ({
            ...item,
            items:
              item.items?.map((subItem) => ({
                ...subItem,
                link: subItem.link?.startsWith('/')
                  ? getFlotiqMainPageUrl('/en' + subItem.link)
                  : subItem.link,
              })) || [],
          })),
        }
      : footerData;

  return {
    isLoading: isLoading,
    footer: finalFooter,
  };
};
